// deps
import { Box } from "@chakra-ui/react";
import { useDatePicker } from "@react-aria/datepicker";
import { useDatePickerState } from "@react-stately/datepicker";
import { forwardRef, useRef } from "react";

// components
import CalendarDateTime from "./CalendarDateTime";
import PopoverContainer from "./Popover";

const DatePicker = forwardRef(
  /**
   * @typedef Props
   * @property {string} name
   * @property {import("@internationalized/date").CalendarDateTime|import("@internationalized/date").CalendarDate | undefined} [value]
   * @property {string} [placeholder]
   * @property {"day" | "hour" | "minute" | "second"} granularity
   * @property {import("@internationalized/date").CalendarDateTime|import("@internationalized/date").CalendarDate | undefined} [minValue]
   * @property {import("@internationalized/date").CalendarDateTime|import("@internationalized/date").CalendarDate | undefined} [maxValue]
   * @property {boolean} [shouldCloseOnSelect]
   * @property {boolean} [shouldCloseOnDaySelect]
   * @property {boolean} [shouldAutoOpenPicker]
   * @property {boolean} [enableKeyboardInput]
   * @property {boolean} [shouldRenderClearButton]
   * @property {boolean} [shouldRenderTodayButton]
   * @property {boolean} [shouldChangeSelectedDateOnChangeMonthOrYear]
   * @property {boolean} [isDisabled]
   * @property {string} [minWidth]
   * @property {(event: any) => void} onChange
   * @property {(event: any) => void} [onChangeFocusDate]
   * @property {(event: any) => void} [onFocus]
   * @property {object} [boxProps]
   */

  /**
   * @param {import("react").PropsWithChildren<Props>} props
   * @returns {import("react").FunctionComponentElement<Props>}
   */
  function DatePicker(props, ref) {
    let state = useDatePickerState(props);

    let inputGroupRef = useRef(null);
    let { groupProps, fieldProps, buttonProps, dialogProps, calendarProps } =
      useDatePicker(props, state, inputGroupRef);

    function handleFocus(event) {
      if (props.shouldAutoOpenPicker && !props.enableKeyboardInput) {
        state.setOpen(true);
      }

      props?.onFocus?.(event);
    }

    return (
      <Box
        ref={ref}
        {...props.boxProps}
        position="relative"
        display="inline-flex"
        flexDirection="column"
        minWidth={props.minWidth}>
        <PopoverContainer
          {...dialogProps}
          groupProps={groupProps}
          inputGroupRef={inputGroupRef}
          fieldProps={fieldProps}
          buttonProps={buttonProps}
          handleFocus={handleFocus}
          state={state}
          isOpen={state.isOpen}
          onClose={() => state.setOpen(false)}>
          <CalendarDateTime
            state={state}
            calendarProps={calendarProps}
            onChange={props.onChange}
            shouldCloseOnDaySelect={props.shouldCloseOnDaySelect}
            shouldRenderClearButton={props.shouldRenderClearButton}
            shouldRenderTodayButton={props.shouldRenderTodayButton}
            shouldChangeSelectedDateOnChangeMonthOrYear={
              props.shouldChangeSelectedDateOnChangeMonthOrYear
            }
            onChangeFocusDate={props.onChangeFocusDate}
          />
        </PopoverContainer>
      </Box>
    );
  },
);

export default DatePicker;
